import { memo, useCallback, useMemo, type ChangeEvent, type FC } from "react";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import CampaignStepCard from "../../../shared/CampaignStepCard";
import CampaignPaginationFooter from "../../../shared/CampaignPaginationFooter";
import MetaTemplateGuide from "../components/MetaTemplateGuide";
import WhatsAppTemplateCard from "../components/WhatsAppTemplateCard";
import type { WhatsAppTemplatesState } from "../hooks/UseWhatsAppTemplates";
import { TEMPLATE_STATUS_FILTERS, WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type { WhatsAppTemplate, WhatsAppTemplateStatusFilter } from "../WhatsAppCampaignTypes";

const STEP_KEY = `${WHATSAPP_EDITOR_KEY}.chooseTemplate`;

interface ChooseWhatsAppTemplateStepProps {
  templatesState: WhatsAppTemplatesState;
  onUse: (_template: WhatsAppTemplate) => void;
}

const ChooseWhatsAppTemplateStep: FC<ChooseWhatsAppTemplateStepProps> = ({
  templatesState,
  onUse,
}) => {
  const { t } = useTranslation();
  const {
    templates,
    loadStatus,
    isConnected,
    errorMessage,
    metaUrl,
    hasTemplates,
    searchTerm,
    statusFilter,
    currentPage,
    totalPages,
    setSearchTerm,
    setStatusFilter,
    goToPreviousPage,
    goToNextPage,
    reload,
  } = templatesState;
  const isLoaded = loadStatus === "success";

  const filterOptions = useMemo(
    () =>
      TEMPLATE_STATUS_FILTERS.map(filter => ({
        value: filter,
        label: t(`${STEP_KEY}.filters.${filter}`),
      })),
    [t]
  );

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value),
    [setSearchTerm]
  );

  const handleFilterChange = useCallback(
    (value: WhatsAppTemplateStatusFilter) => setStatusFilter(value),
    [setStatusFilter]
  );

  return (
    <CampaignStepCard
      wide
      title={t(`${STEP_KEY}.title`)}
      subtitle={t(`${STEP_KEY}.subtitle`)}
      footer={
        isLoaded && templates.length > 0 && (
          <CampaignPaginationFooter
            currentPage={currentPage}
            totalPages={totalPages}
            onPrev={goToPreviousPage}
            onNext={goToNextPage}
          />
        )
      }
    >
      <MetaTemplateGuide metaUrl={metaUrl} />

      {loadStatus === "loading" && (
        <p role="status" className="wa-template-library__empty">
          {t(`${STEP_KEY}.loading`)}
        </p>
      )}

      {loadStatus === "error" && (
        <div role="alert" className="wa-template-library__empty">
          <p>
            {errorMessage ??
              t(isConnected ? `${STEP_KEY}.loadFailed` : `${STEP_KEY}.notConnected`)}
          </p>
          {isConnected && (
            <button
              type="button"
              className="button button--outline campaign-flow__action"
              onClick={reload}
            >
              {t(`${STEP_KEY}.retry`)}
            </button>
          )}
        </div>
      )}

      {isLoaded && (
        <>
          <div className="wa-template-library__toolbar">
            <StyledTextInput
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={t(`${STEP_KEY}.searchPlaceholder`)}
              prefix={<Search size={16} aria-hidden />}
              className="campaign-flow__input wa-template-library__search"
              aria-label={t(`${STEP_KEY}.searchPlaceholder`)}
            />
            <StyledDropdown
              value={statusFilter}
              options={filterOptions}
              onChange={handleFilterChange}
              aria-label={t(`${STEP_KEY}.filterLabel`)}
              className="campaign-flow__select wa-template-library__filter"
              popupClassName="campaign-flow__popup"
            />
          </div>

          {templates.length ? (
            <div className="wa-template-library__grid">
              {templates.map(template => (
                <WhatsAppTemplateCard key={template.id} template={template} onUse={onUse} />
              ))}
            </div>
          ) : (
            <p className="wa-template-library__empty">
              {t(hasTemplates ? `${STEP_KEY}.noResults` : `${STEP_KEY}.noTemplates`)}
            </p>
          )}
        </>
      )}
    </CampaignStepCard>
  );
};

export default memo(ChooseWhatsAppTemplateStep);
